import type { StayLabels } from "@/lib/airbnb/stay-labels";
import { parsePositiveIntParam } from "@/lib/routes/admin-action-links";
import type {
  IncidentalsBilling,
  ReservationMethod,
  RoomTaxBilling,
} from "@/lib/venue-messaging/contract-request-types";
import { Hotel, Presentation, UtensilsCrossed, type LucideIcon } from "lucide-react";
import type { OfferDatesMatch } from "./offer-date-match";
import { ProposalGrouping, ProposalSort, ProposalTab, SortDirection, SortField } from "../_types/ui-types";

/**
 * Share of an offer's nights that must fall inside an event date option for the offer to count
 * as quoted for that option (see `classifyOfferDates`). Half tolerates a one-night shift or an
 * offer inside a wider requested window without matching an unrelated week.
 */
export const OFFER_DATES_MIN_OVERLAP_RATIO = 0.5;

/** Proposal-card copy for each {@link OfferDatesMatch}. */
export const OFFER_DATES_LABELS: Record<OfferDatesMatch, string> = {
  preferred: "Preferred dates",
  alternate: "Alternate dates",
  hotel_proposed: "Hotel-proposed dates",
};

/** Number of proposals to fetch per page. */
export const PAGE_SIZE = 12;

/**
 * Rows requested per `ema_messages` page in the venue-reply rollup (ENG-426). The server honours a
 * request this size, so a short page means end-of-results.
 */
export const VENUE_REPLY_MESSAGE_PAGE_SIZE = 1000;

/**
 * Hard cap on message pages in the venue-reply rollup, so an event with pathological thread volume
 * cannot spin a server action. Low because our own outbound is excluded in SQL, which keeps a
 * realistic event inside one page.
 */
export const VENUE_REPLY_MAX_MESSAGE_PAGES = 5;

/**
 * Cap on `ema_candidate_threads` rows read per rollup call.
 *
 * Sized for an EVENT, not a page — the "Last reply" sort makes the call event-wide. Largest
 * observed is 538 visible threads and p95 is 123, so this is ~3x headroom. Hitting it logs.
 */
export const VENUE_REPLY_MAX_THREADS = 1500;

/**
 * Thread ids per `ema_messages` query. They go into a PostgREST `.in()` on the URL, so an
 * event-wide call cannot put all {@link VENUE_REPLY_MAX_THREADS} of them in one request without
 * risking the URL length limit — the scan batches instead.
 */
export const VENUE_REPLY_THREAD_BATCH = 400;

/**
 * DOM id of a table row's stage-pill row. Shared so `ProposalTableRow`'s `aria-describedby` and
 * `StageCell`'s rendered id cannot drift — if they did, the reference would silently resolve to
 * nothing and screen readers would lose the pills again with no visible symptom.
 *
 * @param cardKey - The card's stable `cardKey`.
 * @returns The element id.
 */
export function stagePillsDomId(cardKey: string): string {
  return `stage-pills-${cardKey}`;
}

/** localStorage key holding the per-venue "emails last seen" instants (ENG-426). */
export const VENUE_EMAILS_SEEN_STORAGE_KEY = "nowadays.venue-emails-seen.v1";

/**
 * Query param that seeds the grid's search box, e.g. `?venue=Marriott%20Marquis`.
 *
 * There is no route for a single proposal (each one opens in a modal on this
 * page), so this is the deep link: the grid, narrowed to one venue. The command
 * palette's proposal rows are built on it. A numeric value is not search text:
 * it is the venue detail overlay's venue id (read by `useVenueSelection`), which
 * Slack "View" links and the admin palette's proposal actions carry.
 */
export const PROPOSAL_SEARCH_PARAM = "venue";

/**
 * Reads {@link PROPOSAL_SEARCH_PARAM} out of a route's `searchParams` as search text.
 *
 * A repeated param (`?venue=a&venue=b`) arrives as an array. Joining it would
 * put "a,b" in the search box and narrow the grid to nothing, so take the first
 * and ignore the rest. A positive integer is the overlay's venue id, not text,
 * so it seeds no search and the grid behind the overlay lists the whole event.
 *
 * @param params - The route's resolved `searchParams`.
 * @returns The venue text, or an empty string when there is none or it is a venue id.
 * @throws Never.
 */
export function readVenueParam(params: { [PROPOSAL_SEARCH_PARAM]?: string | string[] }): string {
  const raw = params[PROPOSAL_SEARCH_PARAM];
  const value = (Array.isArray(raw) ? raw[0] : raw) ?? "";
  return parsePositiveIntParam(value) === null ? value : "";
}

/**
 * Warning toasts for an admin palette proposal action (negotiate, request contract,
 * decline) that lands on a proposal the sidebar would not offer that control for.
 */
export const PROPOSAL_LANDING_TOAST = {
  noProposal: "This venue has no proposal on this event.",
  loadFailed: "Couldn't load this proposal. Try again.",
  alreadyDeclined: "This proposal is already declined.",
  declined: "This proposal was declined. Move it back to quoted first.",
  unavailable: "This venue is marked unavailable for this event.",
  noQuote: "This venue hasn't sent a quote yet.",
  contractRequested: "A contract was already requested from this venue.",
  noRequirementGaps: "This proposal already covers what the event asked for.",
} as const;

/**
 * Line icon + tooltip label per venue type, shown before the venue name on
 * both the card and the table's Venue cell so the category reads at a glance.
 * Only the known types get an icon; anything else (airbnb, villa, etc.)
 * renders the name with no leading glyph.
 */
export const VENUE_TYPE_META: Record<string, { Icon: LucideIcon; label: string }> = {
  hotel: { Icon: Hotel, label: "Hotel" },
  event_space: { Icon: Presentation, label: "Event space" },
  restaurant: { Icon: UtensilsCrossed, label: "Restaurant" },
};

/**
 * The icon and label for a venue type, with the hotel label taken from the active wording
 * register ("Stay" on the Airbnb demo). Read this rather than indexing {@link VENUE_TYPE_META}
 * directly anywhere the label reaches the screen.
 *
 * @param venueType - The raw `venue_type` value, or null.
 * @param labels - The active wording register.
 * @returns The meta for that type, or null when the type is unknown.
 */
export function venueTypeMeta(
  venueType: string | null | undefined,
  labels: StayLabels
): { Icon: LucideIcon; label: string } | null {
  if (!venueType) return null;
  const meta = VENUE_TYPE_META[venueType];
  if (!meta) return null;
  return venueType === "hotel" ? { ...meta, label: labels.venueTypeOption } : meta;
}

/**
 * Fewest sibling quotes an event needs before the F&B peer statistic means anything.
 * With two quotes a single missing F&B line is a 50% "majority" and says nothing.
 */
export const PEER_QUOTE_FLOOR = 4;

/**
 * Share of an event's quotes that must price a line before a quote lacking it reads as
 * anomalous rather than ordinary.
 *
 * Without this the wording escalates on any sibling at all — "though 2 of the 10 other quotes
 * do" is true but says nothing, and firing it on 144 cards instead of ~83 dilutes the one
 * message that should mean "we probably mis-read this quote".
 */
export const PEER_MAJORITY_SHARE = 0.7;

/**
 * Fewest distinct meeting rooms a quote must itemise before a shortfall against the ask is
 * believable.
 *
 * Load-bearing, not a tuning knob: 79% of quotes that price meeting rooms name exactly one,
 * regardless of how much space the venue actually offered — a ballroom plus two breakouts
 * bundled into one priced line is stored as one room. Below this floor the comparison
 * measures extraction granularity rather than venue behaviour, which is why an earlier
 * unguarded version of this rule produced 202 flags where this one produces 34.
 */
export const MEETING_ROOM_ITEMISATION_FLOOR = 2;

/**
 * Longest stretch of a venue's own note quoted back in a requirement-gap tooltip.
 *
 * The note is the whole point of the "in notes only" pill — a planner who reads
 * "£8,000 F&B min. Not currently included in proposal." knows instantly what the total is
 * missing, and one who reads "All inclusive" knows to ignore it. But these run to hundreds
 * of characters (menu lists, all-inclusive inventories), and the tooltip sits over a card.
 * Long enough to carry the first clause, which is where venues put the answer.
 */
export const VENUE_NOTE_TOOLTIP_MAX_CHARS = 160;

/** Keep meeting-space notes aligned with the requirement-gap tooltip's venue-copy limit. */
export const SPACE_NOTE_MAX_CHARS = VENUE_NOTE_TOOLTIP_MAX_CHARS;

/** Horizontal distance used when the outstanding modal changes steps. */
export const OUTSTANDING_STEP_SLIDE_PX = 24;

/** Shared timing for the outstanding modal's table/composer transition. */
export const OUTSTANDING_STEP_TRANSITION = {
  duration: 0.22,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number],
};

/** Milliseconds in a calendar day; used only after dates are normalized to local noon. */
export const MILLISECONDS_PER_DAY = 86_400_000;

/** Number of skeleton placeholders shown while the initial page loads, in both the card grid and the table. */
export const PAGE_SIZE_SKELETON = 8;

/** Lowest selectable value on the rating filter's fixed slider domain. */
export const MIN_STAR_RATING = 1;

/** Highest selectable value on the rating filter's fixed slider domain. */
export const MAX_STAR_RATING = 5;

/**
 * Hover dwell (ms) before a proposal's "View quote" button warms its quote.
 * A grid page mounts dozens of these, so prefetching on the first hover event
 * would queue a server action per card for a mouse merely crossing the list on
 * its way elsewhere. Long enough to read as intent, short enough that the modal
 * still opens warm for anyone who actually stops to click.
 */
export const QUOTE_PREFETCH_HOVER_DELAY_MS = 120;

/**
 * How long a fetched quote stays fresh. Without it every hover would re-issue the
 * fetch (React Query treats data as stale immediately by default), which is exactly
 * the per-card request storm the hover-intent delay exists to prevent. A revision
 * changes the query key, so this never masks an updated quote.
 */
export const VENUE_QUOTE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * React Query key for an event's normalized outstanding requirements.
 *
 * @param eventId - Event whose requirements are being loaded.
 * @returns Stable query key scoped to the event.
 */
export const outstandingDataQueryKey = {
  event: (eventId: number) => ["proposal-outstanding-data", eventId] as const,
};

/**
 * React Query key for one venue offer's full quote. Shared by every surface that
 * opens quote details (the grid card, the table row, the venue sidebar), so a quote
 * warmed by hovering a card is already in cache when the sidebar opens the same offer.
 * `revision` participates in the key so a revised quote is a new entry rather than a
 * stale hit.
 */
export const venueQuoteQueryKey = {
  offer: (eventId: number, venueStaticId: number, quoteGroupId: string | null, revision: string) =>
    ["venue-quote", eventId, venueStaticId, quoteGroupId ?? "headline", revision] as const,
};

/**
/**
 * Tab definitions for the proposals page, ordered to read as the sourcing journey:
 * see everything → save the ones you like → track RFPs you've sent → outcomes.
 *
 * `id` is the stable internal key (unchanged from the original tabs so the package
 * and compare logic that keys on `"received"` keeps working); `label` is the
 * user-facing lifecycle verb; `description` is the plain-language one-liner shown
 * persistently under the active tab so a non-planner understands each stage.
 */
export const TABS: { id: ProposalTab; label: string; description: string }[] = [
  { id: "all", label: "All", description: "Every venue on this event, at any stage." },
  { id: "shortlisted", label: "Bookmarked", description: "Venues you've saved to revisit later." },
  { id: "packages", label: "Packages", description: "Your saved packages of venues to compare and share." },
];

/**
 * Group-by options for the venue grid, in dropdown order. "status" is the
 * per-tab default sectioning; the rest regroup by venue attributes.
 */
export const GROUPING_OPTIONS: { value: ProposalGrouping; label: string }[] = [
  { value: "status", label: "Status" },
  { value: "venueType", label: "Venue type" },
  { value: "city", label: "City" },
  { value: "starRating", label: "Star rating" },
  { value: "preferred", label: "Preferred" },
  { value: "venue", label: "Venue" },
  { value: "none", label: "None" },
];

/** A single column's config in {@link PROPOSAL_TABLE_COLUMNS}. */
export interface ProposalTableColumn {
  id:
    | "venue"
    | "stage"
    | "dates"
    | "address"
    | "distance"
    | "rate"
    | "total"
    | "guestroomCost"
    | "guestroomNotes"
    | "meetingRoomCost"
    | "meetingRoomNotes"
    | "foodAndBeverageCost"
    | "foodAndBeverageNotes"
    | "otherCosts"
    | "rooms"
    | "seating"
    | "cuisine"
    | "rating"
    | "decision"
    | "actions";
  label: string;
  widthClass: string;
}

/**
 * Column config for the proposals table view (`ProposalsTable`/`ProposalTableRow`),
 * in left-to-right order. Every column shows on every tab that reaches the
 * table (only "all" and "shortlisted" — "packages" renders `PackagesSection`
 * instead and never mounts a grid/table). An earlier version of this config
 * gated columns per-tab against `ProposalTab`'s full value set (including
 * "received"/"in-progress"/"unavailable"/"declined", which were consolidated
 * away as selectable tabs in this redesign and are never actually passed as
 * the active tab), which silently hid Stage on Shortlisted, Decision on All,
 * and the bookmark column on Shortlisted. Each cell already renders its own
 * empty state (`EmptyCellValue`) when a proposal has nothing for that column,
 * so no tab-based suppression is needed on top of that.
 *
 * Most columns match what `ProposalCard` renders unconditionally on the card
 * view too. The itemized cost breakdown (Address/Distance/Guestroom/Meeting
 * room/F&B/Other costs/Seating/Cuisine) is table-only — those fields exist on
 * `ProposalCard` (see its doc comment) but have no card equivalent, matching
 * the legacy customer-portal `/quote-dashboard` grid's column set without
 * porting its data model.
 *
 * Table-only: this file also backs the external-share export surface
 * (`ShareProposalsGrid`/`ShareFormatPicker`), so this constant must never be
 * reused there, and its `id`s must never point at internal-only fields
 * (`declineReasonNote`, `customerDecisionStatusReason`, `decisionEmail*`).
 */
/**
 * The one column pinned via `sticky left-0` in both the table's header
 * (`ProposalsTable.tsx`) and body rows (`ProposalTableRow.tsx`) — a single
 * source of truth so the two can't independently drift on which column id
 * they treat as sticky (they still choose their own z-index/background,
 * since the header must layer above scrolled body cells).
 */
export const STICKY_COLUMN_ID: ProposalTableColumn["id"] = "venue";

export const PROPOSAL_TABLE_COLUMNS: ProposalTableColumn[] = [
  // Wide enough that a long venue name usually fits on one or two wrapped
  // lines rather than needing the column's own scrollbar — VenueCell wraps
  // rather than truncating, so a name that's still too long just grows the row.
  { id: "venue", label: "Venue", widthClass: "w-80" },
  // Wide enough for the longest single status pill ("Contract · In review") to sit on one line.
  // Pills now wrap within `max-w-full` (see PROPOSAL_STATUS_PILL_BASE_CLASSNAME), so a pill wider
  // than this — the replied pill, which carries a date — grows the row instead of bleeding into
  // the next cell.
  // The requirement-gap labels (ENG-354) are the longest text this column carries and some are
  // interpolated with counts, so their width depends on the data. Measured in Chromium against
  // this column's 160px content box (width - TableCell `p-4`):
  //   "Only 10 of 100 meeting rooms"    205px   <- worst realistic case, 3-digit room counts
  //   "No meeting space in this total"  201px
  //   "Meeting space in notes only"     191px
  //   "No guestrooms in this total"     185px
  //   "2 lines in notes only"           145px
  //   "No F&B in this total"            143px
  //   "2 gaps in this total"            137px
  // The first four wrap to two lines rather than bleeding into Dates, which is what
  // `max-w-full`/`whitespace-normal` above is for.
  { id: "stage", label: "Stage", widthClass: "w-48" },
  { id: "dates", label: "Dates", widthClass: "w-40" },
  { id: "address", label: "Address", widthClass: "w-56" },
  { id: "distance", label: "Distance", widthClass: "w-56" },
  { id: "rate", label: "Nightly rate", widthClass: "w-28" },
  // Wide enough for the nowrap "Added Mmm DD, YYYY" line under the price.
  { id: "total", label: "Total price", widthClass: "w-40" },
  { id: "guestroomCost", label: "Guestroom cost", widthClass: "w-36" },
  { id: "guestroomNotes", label: "Guestroom notes", widthClass: "w-[25rem]" },
  { id: "meetingRoomCost", label: "Meeting room cost", widthClass: "w-36" },
  { id: "meetingRoomNotes", label: "Meeting room notes", widthClass: "w-[25rem]" },
  { id: "foodAndBeverageCost", label: "F&B cost", widthClass: "w-36" },
  { id: "foodAndBeverageNotes", label: "F&B notes", widthClass: "w-[25rem]" },
  { id: "otherCosts", label: "Other costs", widthClass: "w-[32rem]" },
  // Wide enough for "### guest" / "### meeting" on one line each (RoomsCell is nowrap).
  { id: "rooms", label: "Rooms", widthClass: "w-32" },
  { id: "seating", label: "Seating", widthClass: "w-32" },
  { id: "cuisine", label: "Cuisine", widthClass: "w-40" },
  { id: "rating", label: "Rating", widthClass: "w-20" },
  // Wide enough for the common "Hotel notified: negotiate · Mmm DD, YYYY" label without truncating.
  { id: "decision", label: "Decision", widthClass: "w-96" },
  // Two 44×44px buttons (the "View quote" icon and the kebab — 44px is the a11y
  // touch-target minimum) plus this column's own tighter padding (see
  // ProposalTableRow's "actions"-only cell className) need ~100px; w-28 gives it
  // room without stealing width other columns need.
  { id: "actions", label: "Actions", widthClass: "w-28" },
];

/**
 * Sortable fields for the main proposals grid, in dropdown order. The grid picks a
 * field here and a separate arrow toggles the direction. `defaultDirection` is the
 * sensible starting direction applied when the user switches TO a field (e.g. price
 * low→high, rating high→low, name A→Z), after which the arrow flips it.
 */
/**
 * The proposals grid's default sort: the venues that have written in most recently, first.
 *
 * ONE constant because the client's initial state and the SSR seed must agree — if they disagree the
 * server renders one order and the client immediately re-sorts into another, which reads as the page
 * jumping on load. They were two independent literals before.
 *
 * Venues that have never replied keep their "Date added" ordering behind the ones that have (see
 * `sortProposalCardsBy`), so an event with no replies yet looks exactly as it did before.
 */
export const DEFAULT_PROPOSAL_SORT: ProposalSort = { field: "latestVenueReplyAt", direction: "desc" };

export const SORT_FIELDS: { value: SortField; label: string; defaultDirection: SortDirection }[] = [
  { value: "proposalDate", label: "Date added", defaultDirection: "desc" },
  // "Last reply", not "Newest reply": the control renders "Sort by {label} · {direction}", so a
  // label with a direction baked in reads as a contradiction when flipped to ascending, and the
  // screen-reader string ("Sorted ascending by newest reply") worse. Matches the overlay chip copy.
  // Still defaults to desc, which is the useful direction.
  { value: "latestVenueReplyAt", label: "Last reply", defaultDirection: "desc" },
  { value: "grandTotal", label: "Total price", defaultDirection: "asc" },
  { value: "nightlyRate", label: "Nightly rate", defaultDirection: "asc" },
  { value: "starRating", label: "Star rating", defaultDirection: "desc" },
  { value: "guestRooms", label: "Guest rooms", defaultDirection: "desc" },
  { value: "meetingRooms", label: "Meeting rooms", defaultDirection: "desc" },
  { value: "name", label: "Name", defaultDirection: "asc" },
];

/**
 * Maximum character length of a negotiation note. Enforced both server-side
 * (in `updateNegotiationStatus`) and client-side (`maxLength` on the textarea)
 * to bound the size of `customer_decision_status_reason` writes.
 */
export const NEGOTIATION_NOTE_MAX_LENGTH = 4000;

/**
 * Selectable decline-reason tags shown in the Decline modal. `key` is the
 * stable value persisted to `decline_reason_tags` (validated server-side
 * against this allowlist); `label` is the user-facing chip text. Edit this
 * list to change the offered reasons.
 */
export const DECLINE_REASON_TAGS: { key: string; label: string }[] = [
  { key: "too_expensive", label: "Too expensive" },
  { key: "location", label: "Location/area" },
  { key: "dates_unavailable", label: "Dates unavailable" },
  { key: "capacity_too_small", label: "Capacity too small" },
  { key: "missing_amenities", label: "Missing amenities" },
  { key: "slow_response", label: "Slow/poor response" },
  { key: "chose_another", label: "Client chose another" },
  { key: "other", label: "Other" },
];

/** Set of valid decline-tag keys for server-side validation. */
export const DECLINE_REASON_TAG_KEYS: ReadonlySet<string> = new Set(DECLINE_REASON_TAGS.map((t) => t.key));

/**
 * Selectable reinstate-reason tags shown in the "Move back to quoted" modal.
 * Same shape/validation contract as {@link DECLINE_REASON_TAGS}: `key` is the
 * stable value validated server-side and sent to Slack; `label` is the chip text.
 */
export const REINSTATE_REASON_TAGS: { key: string; label: string }[] = [
  { key: "price_improved", label: "Price improved" },
  { key: "dates_now_work", label: "Dates now work" },
  { key: "negotiated_better_terms", label: "Negotiated better terms" },
  { key: "client_reconsidered", label: "Client reconsidered" },
  { key: "declined_by_mistake", label: "Declined by mistake" },
  { key: "other", label: "Other" },
];

/** Set of valid reinstate-tag keys for server-side validation. */
export const REINSTATE_REASON_TAG_KEYS: ReadonlySet<string> = new Set(REINSTATE_REASON_TAGS.map((t) => t.key));

/** Copy strings for the Decline modal and its toast feedback. */
export const DECLINE_MODAL_COPY = {
  title: "Decline proposal",
  tagsLabel: "Why are you declining? Pick at least one",
  notePlaceholder: "Add any extra context (optional)…",
  notifyDisclosure: "The venue will be notified that you're passing.",
  declineLabel: "Decline proposal",
  cancelLabel: "Cancel",
  toastDeclined: "Proposal declined",
  toastError: "Something went wrong. Please try again.",
} as const;

/** Copy strings for the Undecline modal and its toast feedback. */
export const UNDO_DECLINE_COPY = {
  title: "Move back to quoted",
  tagsLabel: "Why are you reinstating? Pick at least one",
  notePlaceholder: "Add any extra context (optional)…",
  confirmLabel: "Move to quoted",
  cancelLabel: "Cancel",
  toastUndeclined: "Proposal moved back to quoted",
  toastError: "Something went wrong. Please try again.",
  recentDeclineWarningTitle: "You recently declined this proposal",
  recentDeclineWarningBody: "Are you sure you want to reinstate it? The hotel will be notified either way.",
  recentDeclineWarningContinue: "Yes, continue",
} as const;

/**
 * If a proposal was declined within this many hours, `UndoDeclineModal` shows
 * a blocking confirmation step before the reason form — mitigates accidental
 * decline→undecline whiplash sending contradictory emails to the hotel.
 * Mirrors (but is intentionally decoupled from) the server-side oscillation
 * guard's window in `src/lib/venue-messaging/constants.ts`.
 */
export const RECENT_DECLINE_WARNING_HOURS = 24;

/**
 * `venue_communications.external_source` for client-triggered negotiation-email
 * sends (kickoff + follow-up), distinct from `BUMP_EMAIL_SOURCE` (which stays
 * the `source` value so these sends still count on the venue activity feed) —
 * used only for the dedup key and the rate-limit count below, so a client
 * negotiation send never consumes the staff bump cap.
 */
export const NEGOTIATION_EMAIL_EXTERNAL_SOURCE = "client-negotiation-send";

/** `venue_communications.communication_type` for a negotiation-email send. */
export const NEGOTIATION_EMAIL_COMMUNICATION_TYPE = "negotiation_email";

/** Rolling window (hours) the negotiation-email rate limit counts sends over. */
export const NEGOTIATION_EMAIL_RATE_LIMIT_WINDOW_HOURS = 24;

/** Max negotiation-email sends (kickoff + follow-up combined) per venue+event within the window. */
export const NEGOTIATION_EMAIL_RATE_LIMIT_MAX = 3;

/** Copy strings for the Negotiate notes modal and its toast feedback. */
export const NEGOTIATE_MODAL_COPY = {
  title: "Negotiation notes",
  concessionGridLabel: "What would you like to ask for?",
  selectAllLabel: "Select all",
  deselectAllLabel: "Deselect all",
  disclosure:
    "We'll draft and send this to the venue automatically, right away — there's no preview or review step before it goes out.",
  placeholder:
    "Add notes about your negotiation (optional), e.g. target rate, or different numbers than the defaults above (more room nights, a higher discount, etc.)…",
  negotiateLabel: "Negotiate",
  followUpLabel: "Send request",
  /** Label for the card/menu-level CTA once a candidate is already negotiating — distinct from `followUpLabel`, the modal's own submit button. */
  sendAnotherRequestLabel: "Send another request",
  // The actual send is deferred (see `after()` in negotiation-status-actions.ts) and can still
  // resolve as rate-limited or failed — these toasts intentionally avoid promising delivery.
  toastStarted: "Negotiation started — we'll email the venue shortly",
  toastFollowUpSent: "Request queued — we'll email the venue shortly",
  toastCanceled: "Negotiation canceled",
  toastError: "Something went wrong. Please try again.",
  discardChangesTitle: "Discard changes?",
  discardChangesMessage: "Your selected asks and note will be lost.",
  discardChangesConfirmLabel: "Yes, discard changes",
  discardChangesCancelLabel: "Cancel, continue negotiating",
  cancelConfirmTitle: "Cancel negotiation?",
  cancelConfirmMessage:
    "This proposal will move back to quoted. Any request already emailed to the venue can't be unsent. You can start a new negotiation anytime.",
  cancelConfirmConfirmLabel: "Yes, cancel negotiation",
  cancelConfirmCancelLabel: "No, keep negotiating",
} as const;

/**
 * Max length for each short text field in the contract-request form (name,
 * role, phone, address, tax number, etc). Enforced server-side in
 * `requestContract` and client-side via `maxLength`.
 */
export const CONTRACT_TEXT_FIELD_MAX_LENGTH = 200;

/** Max length for the contract-request form's note to the hotel. */
export const CONTRACT_COMMENTS_MAX_LENGTH = 2000;

/**
 * Client-facing option labels for the contract-request form's exclusive
 * choices. `key`s are the stable values persisted to `contract_requests`
 * (validated server-side against the same union the DB CHECK constraints
 * enforce); the hotel-facing wording for the same keys lives in
 * `venue-messaging/constants.ts` label maps.
 */
export const CONTRACT_RESERVATION_METHOD_OPTIONS: { key: ReservationMethod; label: string }[] = [
  { key: "rooming_list", label: "Rooming list" },
  { key: "individual_call_in", label: "Individual call-in" },
];

/** Client-facing choices for who covers room and tax. */
export const CONTRACT_ROOM_TAX_OPTIONS: { key: RoomTaxBilling; label: string }[] = [
  { key: "master_account", label: "Master account" },
  { key: "guest_pays_own", label: "Guests pay own" },
];

/** Client-facing choices for where incidentals go. */
export const CONTRACT_INCIDENTALS_OPTIONS: { key: IncidentalsBilling; label: string }[] = [
  { key: "own_account", label: "Guests' own accounts" },
  { key: "master_account", label: "Master account" },
];

/** Client-facing choices for whether Nowadays handles billing. */
export const CONTRACT_BILLING_HANDLED_OPTIONS: { key: boolean; label: string }[] = [
  { key: true, label: "Yes, via Nowadays (3% processing fee)" },
  { key: false, label: "No, we'll settle directly" },
];

/** Copy for the contract-request modal and its toast feedback. */
export const CONTRACT_REQUEST_MODAL_COPY = {
  title: "Request contract",
  eventSectionLabel: "Event details",
  showEventDetailsLabel: "Show event details",
  hideEventDetailsLabel: "Hide event details",
  addMissingEventDetailsLabel: "Add missing details",
  eventDetailsLoadError: "Unable to load event details",
  partySectionLabel: "Contract details",
  editPartyLabel: "Edit contract details",
  partyHint: "From your profile and your last contract request.",
  nameLabel: "Name",
  roleLabel: "Role",
  emailLabel: "Email",
  phoneLabel: "Phone",
  companyNameLabel: "Company name",
  streetAddressLabel: "Street address",
  cityLabel: "City",
  stateRegionLabel: "State / Region",
  postalCodeLabel: "Postal code",
  countryLabel: "Country",
  invoicingSectionLabel: "Invoicing & legal",
  taxNumberLabel: "Tax number",
  invoicingEmailLabel: "Invoicing email",
  legalRepresentativeLabel: "Legal representative",
  billingSectionLabel: "Billing instructions",
  reservationMethodLabel: "How will guests make reservations?",
  roomTaxLabel: "Who covers room & tax?",
  incidentalsLabel: "Where do incidentals go?",
  billingHandledLabel: "Should Nowadays handle billing on your behalf?",
  signerSectionLabel: "Signer",
  requesterSignsText: "You'll sign from your Nowadays account.",
  addSignerLabel: "Add a different signer",
  removeSignerLabel: "I'll sign myself",
  signerNameLabel: "Signer name",
  signerTitleLabel: "Signer title",
  signerEmailLabel: "Signer email",
  commentsLabel: "Note to the hotel",
  commentsPlaceholder: "Anything the hotel should know for the contract (optional)",
  requiredHint: "Required",
  invalidEmailHint: "Enter a valid email",
  sendLabel: "Send contract request",
  cancelLabel: "Cancel",
  toastSuccess: "Contract request sent",
  toastError: "Something went wrong. Please try again.",
  toastAlreadyRequested: "A contract has already been requested for this venue.",
} as const;

/**
 * Copy for the screen a client sees once a contract request has gone out. Mirrors the venue
 * message success state: an outcome title, one confirmation line, then what happens next. Each
 * line states only what the send path does (see `send-contract-request-email.ts` and
 * `generate-contract-request-email.ts`); no turnaround is promised, because the product makes none.
 */
export const CONTRACT_REQUEST_NEXT_STEPS_COPY = {
  title: "Contract requested",
  /**
   * The confirmation line. "Sending", not "sent": the email goes out after the response and can fail.
   *
   * @param venueName - The venue the request went to.
   * @returns The confirmation sentence.
   * @throws Never.
   */
  confirmation: (venueName: string): string => `We're sending your request to ${venueName}.`,
  nextStepsLabel: "What happens next",
  draftStep: "We've asked the venue for a draft contract for you to review.",
  repliesStep: "Their replies appear in this venue's Emails section.",
  doneLabel: "Done",
} as const;

/**
 * The line shown beside the send button when a send is rejected: names the
 * count, so you know whether you are one box away or ten, and leads with the verb.
 *
 * @param fieldCount - How many fields failed validation; always at least one when this is shown.
 * @returns The summary line.
 * @throws Never.
 */
export function contractRequestValidationSummary(fieldCount: number): string {
  return `Fix ${fieldCount} ${fieldCount === 1 ? "field" : "fields"} to send`;
}

/** React Query keys for the contract-request modal's event-details query. */
export const contractRequestReviewQueryKey = {
  summary: (eventId: number, eventVenueCandidateId: number) =>
    ["contract-request-review", "summary", eventId, eventVenueCandidateId] as const,
};

/**
 * z-index for the contract-request modal. Lowered from FetchModal's 99999
 * default to leave headroom for the event-details editor stacked above it,
 * same reason RFP's confirmation modal defines its own tier.
 */
export const CONTRACT_REQUEST_MODAL_Z_INDEX = 99980;

/** z-index for the event-details editor opened from the contract-request modal. Below the toast viewport (100001). */
export const CONTRACT_REQUEST_EDIT_MODAL_Z_INDEX = CONTRACT_REQUEST_MODAL_Z_INDEX + 6;

/**
 * How many reason tags or concession ids one decision or negotiation may carry.
 *
 * Membership was checked per element and the array length never was, so a
 * caller could send the same valid id thousands of times and have all of them
 * rendered into the hotel email and the Slack card. Comfortably above the
 * catalog sizes (12 decline reasons, 21 concessions), so a real request from
 * either door cannot reach it.
 */
export const DECISION_REASON_TAGS_MAX = 25;
